'use client';

import React from 'react';
import { useEventListener } from '@react-hookz/web';
import { IconArrowUp } from 'icon/Arrows/arrow-up';
import { IconCornerDownLeft } from 'icon/Arrows/corner-down-left';
import { useTranslations } from 'next-intl';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'ui';
import { isEnvSSR } from 'utils/env';
import { GROUP_HEADING_SELECTOR, GROUP_SELECTOR } from './search-group';

export type SearchDialog = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
};

export function SearchDialog({ children, open, ...props }: SearchDialog) {
  const contentRef = React.useRef<HTMLDivElement>(null);
  useEventListener(
    isEnvSSR ? null : document,
    'keydown',
    (e: KeyboardEvent) => {
      if (!open) {
        return;
      }
      if (!contentRef.current) {
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        contentRef.current
          .querySelector<HTMLButtonElement>('li[aria-selected="true"] button')
          ?.click();
        props.onOpenChange?.(false);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        scrollSelectedIntoView(contentRef.current, 'up');
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        scrollSelectedIntoView(contentRef.current, 'down');
      }
    },
  );
  const t = useTranslations('Blog.Search');
  return (
    <Dialog {...props} open={open}>
      <DialogContent
        className="max-w-xl! gap-0 overflow-hidden p-0! shadow-lg"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className="sr-only">{t('Placeholder')}</DialogTitle>
        </DialogHeader>
        <div ref={contentRef}>
          {children}
          <footer className="hidden items-center gap-4 border-t bg-primary/80 px-4.5 py-3 text-quaternary backdrop-blur-md sm:flex">
            <p className="flex items-center gap-1.5 text-sm font-semibold leading-none">
              <kbd className="rounded-md border bg-secondary p-1.5">
                <IconArrowUp className="size-4" />
              </kbd>
              <kbd className="rounded-md border bg-secondary p-1.5">
                <IconArrowUp className="size-4 rotate-180" />
              </kbd>
              <span className="ps-0.5">{t('Navigation')}</span>
            </p>
            <p className="flex items-center gap-2 text-sm font-semibold leading-none">
              <kbd className="rounded-md border bg-secondary p-1.5">
                <IconCornerDownLeft className="size-4" />
              </kbd>
              <span>{t('Select')}</span>
            </p>
            <p className="flex items-center gap-2 text-sm font-semibold leading-none">
              <kbd className="rounded-md border bg-secondary p-1.5 font-mono font-medium">
                esc
              </kbd>
              <span>{t('Close')}</span>
            </p>
          </footer>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function scrollSelectedIntoView(
  container: HTMLDivElement,
  direction: 'up' | 'down',
) {
  const selected = container.querySelector('li[aria-selected="true"]');
  if (!selected) {
    const first = container.querySelector('li');
    if (first) {
      first?.setAttribute('aria-selected', 'true');
      scrollHeadingIntoView(first);
      first.scrollIntoView({
        block: 'nearest',
      });
    }
  } else {
    const sibling = (
      direction === 'down'
        ? selected.nextElementSibling
        : selected.previousElementSibling
    ) as HTMLLIElement;
    if (sibling) {
      sibling.setAttribute('aria-selected', 'true');
      scrollHeadingIntoView(sibling);
      sibling.scrollIntoView({
        block: 'nearest',
      });
      selected.setAttribute('aria-selected', 'false');
    }
  }
}

function scrollHeadingIntoView(next: HTMLLIElement) {
  if (next === next.parentElement?.firstChild) {
    next
      .closest(GROUP_SELECTOR)
      ?.querySelector(GROUP_HEADING_SELECTOR)
      ?.scrollIntoView({
        block: 'nearest',
      });
  }
}
